import { forwardRef } from 'react'
import { theme } from '../../theme'
import { apply, tw } from 'twind'
import { css } from 'twind/css'

type colors = 'primary' | 'secondary'
type variants = 'solid' | 'outlined' | 'text'
type sizes = 'xs' | 'sm' | 'md' | 'lg'

export interface ButtonProps
  extends Omit<React.ComponentPropsWithoutRef<'button'>, 'disabled'> {
  color?: colors
  variant?: variants
  size?: sizes
  isDisabled?: boolean
  className?: string
}

const baseStyles = apply`
  inline-flex
  justify-center
  items-center
  border
  border-solid
  border-transparent
  bg-transparent
  rounded-sm
  font-semibold
`

const sizeMap = {
  xs: apply`text-sm h-8 px-3 min-w-18`,
  sm: apply`text-sm h-9 px-4 min-w-22`,
  md: apply`text-md h-10 px-5 min-w-24`,
  lg: apply`text-md h-12 px-6 min-w-27`,
}

const getVariantsMap = (color: string) => {
  const backgroundValue = theme(`colors.${color}.02`, { rawValue: true })
  const colorValue = theme(`colors.${color}.03`)

  const hover = css({
    '&:hover': {
      border: `1px solid ${theme(`colors.${color}.02`)}`,
      color: theme(`colors.${color}.02`),
      background: `rgba( ${backgroundValue} ,${theme(`opacity.10`)} )`,
    },
  })

  const focusBase = {
    outline: `2px solid ${colorValue}`,
    'outline-offset': '2px',
  }

  const activeBase = {
    color: theme(`colors.${color}.03`),
    background: `rgba( ${backgroundValue} ,${theme(`opacity.15`)} )`,
  }

  const focusSolid = css({
    '&:focus': focusBase,
  })

  const activeOutline = css({
    '&:active': {
      ...activeBase,
      border: `1px solid ${theme(`colors.${color}.03`)}`,
    },
  })

  const focusOutline = css({
    '&:focus': {
      ...focusBase,
      color: theme(`colors.${color}.03`),
      background: `rgba( ${backgroundValue}, ${theme(`opacity.20`)} )`,
    },
  })

  const activeText = css({
    '&:active': activeBase,
  })

  const focusText = css({
    '&:focus': {
      ...focusBase,
      color: theme(`colors.${color}.03`),
      background: `rgba( ${backgroundValue}, ${theme(`opacity.20`)} )`,
    },
  })

  return {
    solid: apply`
      bg-${color}-01 border-${color}-01 text-neutral-01
      hover:(bg-${color}-02 border-${color}-02) 
      active:(bg-${color}-03 border-${color}-03)
      disabled:(bg-disabled-01 border-disabled-01 text-disabled-02 cursor-not-allowed)
      disabled:hover:(bg-disabled-01 border-disabled-01 text-disabled-02)
      ${focusSolid}
    `,
    outlined: apply`
      border-${color}-01 text-${color}-01 
      hover:bg-${color}-05 
      active:bg-${color}-04
      disabled:(bg-transparent border-disabled-01 text-disabled-02 cursor-not-allowed)
      disabled:hover:(bg-transparent border-disabled-01 text-disabled-02)
      ${hover}
      ${activeOutline}
      ${focusOutline}
   `,
    text: apply`
      text-${color}-01 
      hover:bg-${color}-05 
      active:bg-${color}-04 
      disabled:(bg-transparent text-disabled-02 cursor-not-allowed)
      disabled:hover:(bg-transparent text-disabled-02)
      ${hover}
      ${activeText}
      ${focusText}
    `,
  }
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      color = 'primary',
      size = 'sm',
      variant = 'solid',
      isDisabled = false,
      className,
      children,
      onClick,
      ...props
    },
    ref
  ) => {
    const instanceStyles = css`
      ${baseStyles}
      ${getVariantsMap(color)[variant]}
      ${sizeMap[size]}
    `
    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
      !isDisabled && onClick?.(e)
    }

    return (
      <button
        type="button"
        className={tw(instanceStyles, className)}
        disabled={isDisabled}
        ref={ref}
        onClick={handleClick}
        {...props}
      >
        {children}
      </button>
    )
  }
)

export default Button
